<!--
Communication between the iframe and the parent component is done via
postMessage. The iframe should send a message to the parent component
when it updates the spreadsheet data which can be saved to the backend.

Restoring data into the iframed spreadsheet should be done by sending a postMessage
from the parent component to the iframe after the iframe has loaded.

We should avoid trying to update the spreadsheet data repeatedly from the parent
component. We should use the spreadsheet as the source of truth to avoid any
ugly state synchronisation issues.
-->
<template>
  <iframe
    ref="frame"
    title="Spreadsheet"
    src="/spreadsheet.html"
    style="width: 100%; height: 100%; border: none"
    @load="onFrameLoad"
  ></iframe>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, toRaw } from 'vue'

const props = defineProps({
  model: {
    type: Object,
    default: () => {},
  },
  showTools: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['export', 'loaded', 'saved'])

const frame = ref(null)
let frameWindow = null

const onFrameLoad = () => {
  frameWindow = frame.value?.contentWindow
  if (frameWindow) {
    frameWindow.postMessage(
      {
        type: 'spreadsheet:init',
        payload: {
          config: toRaw(props.model),
          showTools: props.showTools,
        },
      },
      '*',
    )
    emit('loaded')
  }
}

const onMessage = (event) => {
  if (event.data?.type === 'spreadsheet:saved' || event.data?.type === 'spreadsheet:autosaved') {
    emit('export', event.data.payload)
  }
}

onMounted(() => {
  window.addEventListener('message', onMessage)
})

onBeforeUnmount(() => {
  window.removeEventListener('message', onMessage)
})

const requestSave = () => {
  if (frameWindow) {
    frameWindow.postMessage({ type: 'spreadsheet:save' }, '*')
  }
}

defineExpose({ requestSave })
</script>
